import { useMemo } from 'react';
import { costFormater, useForm } from '@common-fe/common-fe';
import { yupResolver } from '@hookform/resolvers/yup';
import _ from 'lodash';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { REQUIRED_TEXT } from '@/common/constants';

import {
  CHANGE_DATE_VALIDATOR,
  DEDUCTIBLE_DATE_VALIDATOR,
  HEALTH_PLAN_CHANGE_DATE_VALIDATOR,
  MIN_PER_PAY_AMOUNT,
} from '../utils/validators';

export enum EnrollmentFormField {
  tpaPartner = 'tpaPartner',
  employer = 'employer',
  employeeId = 'employeeId',
  employerEmployeeId = 'employerEmployeeId',
  partnerEmployeeId = 'partnerEmployeeId',
  ssn = 'ssn',
  lastName = 'lastName',
  type = 'type',
  plan = 'plan',
  healthPlan = 'healthPlan',
  planYear = 'planYear',
  planStartDate = 'planStartDate',
  planEndDate = 'planEndDate',
  enrollmentStatus = 'enrollmentStatus',
  electionAmount = 'electionAmount',
  perPayEmployer = 'perPayEmployer',
  perPayEmployee = 'perPayEmployee',
  limitedUse = 'limitedUse',
  coverageLevel = 'coverageLevel',
  coverageLevelEffectiveDate = 'coverageLevelEffectiveDate',
  deductibleMetDate = 'deductibleMetDate',
  enrollmentDate = 'enrollmentDate',

  healthPlanYear = 'healthPlanYear',
  healthPlanStartDate = 'healthPlanStartDate',
  healthPlanEndDate = 'healthPlanEndDate',
  healthPlanCoverageLevel = 'healthPlanCoverageLevel',
  healthPlanEnrollmentStatus = 'healthPlanEnrollmentStatus',
  healthPlanEnrollmentDate = 'healthPlanEnrollmentDate',
}

export type EnrollmentFormFields = {
  [EnrollmentFormField.tpaPartner]?: string;
  [EnrollmentFormField.employer]?: string;
  [EnrollmentFormField.employeeId]?: string;
  [EnrollmentFormField.employerEmployeeId]?: string | null;
  [EnrollmentFormField.partnerEmployeeId]?: string | null;
  [EnrollmentFormField.ssn]?: string | null;
  [EnrollmentFormField.lastName]?: string;
  [EnrollmentFormField.type]?: string;
  [EnrollmentFormField.plan]?: string;
  [EnrollmentFormField.healthPlan]?: string;
  [EnrollmentFormField.planYear]?: string;
  [EnrollmentFormField.planStartDate]?: string;
  [EnrollmentFormField.planEndDate]?: string;
  [EnrollmentFormField.enrollmentStatus]?: string;
  [EnrollmentFormField.electionAmount]?: string;
  [EnrollmentFormField.perPayEmployer]?: string;
  [EnrollmentFormField.perPayEmployee]?: string;
  [EnrollmentFormField.limitedUse]?: string;
  [EnrollmentFormField.coverageLevel]?: string;
  [EnrollmentFormField.coverageLevelEffectiveDate]?: string;
  [EnrollmentFormField.deductibleMetDate]?: string;
  [EnrollmentFormField.enrollmentDate]?: string;

  [EnrollmentFormField.healthPlanYear]?: string;
  [EnrollmentFormField.healthPlanStartDate]?: string;
  [EnrollmentFormField.healthPlanEndDate]?: string;
  [EnrollmentFormField.healthPlanCoverageLevel]?: string;
  [EnrollmentFormField.healthPlanEnrollmentStatus]?: string;
  [EnrollmentFormField.healthPlanEnrollmentDate]?: string;
};

export type HealthPlanFormFields = {
  type?: string;
  healthPlan?: string;
  healthPlanEnrollmentStatus?: string;
  healthPlanCoverageLevel?: string;
  healthPlanEnrollmentDate?: string;
};

export type EnrollmentFormValidationParams = Partial<{
  minElectionAmount: number;
  maxElectionAmount: number;
  lastCoverageEndDate: string | null;
  planStartDate: string;
  planEndDate: string;
  catchUpAge: number | null;
  employeeAge: number;
  catchUpElection: number | null;
  healthPlanStartDate: string;
  healthPlanEndDate: string;
  contributionMonitoringRequired: boolean;
}>;

export const getEnrollmentFormSchema = (params: EnrollmentFormValidationParams) => {
  const minAmount = costFormater(params?.minElectionAmount || 0, { isPrecision: true });
  const maxAmount = (() => {
    if (params?.catchUpAge && params?.employeeAge && params?.employeeAge >= params?.catchUpAge) {
      return costFormater(_.toNumber(params?.maxElectionAmount) + _.toNumber(params?.catchUpElection) || 0, {
        isPrecision: true,
      });
    }
    return costFormater(params?.maxElectionAmount || 0, { isPrecision: true });
  })();

  const maxAmountValue = (() => {
    if (params?.catchUpAge && params?.employeeAge && params?.employeeAge >= params?.catchUpAge) {
      return _.toNumber(params?.maxElectionAmount) + _.toNumber(params?.catchUpElection);
    }

    return _.toNumber(params?.maxElectionAmount);
  })();

  return yup.object({
    type: VALIDATORS.REQUIRED_STRING,
    plan: VALIDATORS.REQUIRED_STRING,
    enrollmentStatus: VALIDATORS.REQUIRED_STRING,
    electionAmount: yup
      .string()
      .test({
        test: (val) => {
          if (params?.minElectionAmount && _.toNumber(val) < params.minElectionAmount) return false;
          if (maxAmountValue && _.toNumber(val) > maxAmountValue) return false;
          return true;
        },
        message: `Should be in range from ${minAmount} to ${maxAmount}`,
      })
      .required(REQUIRED_TEXT),
    perPayEmployer: yup.string().test({
      test: (val) => {
        if (val && _.toNumber(val) !== 0 && _.toNumber(val) < MIN_PER_PAY_AMOUNT) return false;
        return true;
      },
      message: `Should be more than $${MIN_PER_PAY_AMOUNT}.00`,
    }),
    perPayEmployee: yup.string().test({
      test: (val) => {
        if (val && _.toNumber(val) !== 0 && _.toNumber(val) < MIN_PER_PAY_AMOUNT) return false;
        return true;
      },
      message: `Should be more than $${MIN_PER_PAY_AMOUNT}.00`,
    }),
    coverageLevelEffectiveDate: yup
      .string()
      .when(EnrollmentFormField.coverageLevel, (coverageLevel, schema) => {
        if (!params.contributionMonitoringRequired) {
          return schema.notRequired().nullable();
        }
        const value = Array.isArray(coverageLevel) ? coverageLevel[0] : coverageLevel;
        if (value && value !== '') {
          return CHANGE_DATE_VALIDATOR({
            lastCoverageEndDate: params.lastCoverageEndDate,
            planStartDate: params.planStartDate,
            planEndDate: params.planEndDate,
          });
        }
        return schema.notRequired().nullable();
      }),
    deductibleMetDate: DEDUCTIBLE_DATE_VALIDATOR,
    enrollmentDate: CHANGE_DATE_VALIDATOR({
      lastCoverageEndDate: params.lastCoverageEndDate,
      planStartDate: params.planStartDate,
      planEndDate: params.planEndDate,
    }),
  });
};

export const getHealthPlanOnlySchema = (params: EnrollmentFormValidationParams) => {
  return yup.object({
    type: VALIDATORS.REQUIRED_STRING,
    healthPlan: VALIDATORS.REQUIRED_STRING,
    healthPlanEnrollmentStatus: VALIDATORS.REQUIRED_STRING,
    healthPlanCoverageLevel: VALIDATORS.REQUIRED_STRING,
    healthPlanEnrollmentDate: HEALTH_PLAN_CHANGE_DATE_VALIDATOR({
      healthPlanStartDate: params.healthPlanStartDate,
      healthPlanEndDate: params.healthPlanEndDate,
    }),
  });
};

export const useEnrollmentForm = (
  initialValues?: Partial<EnrollmentFormFields>,
  validationParams?: EnrollmentFormValidationParams,
  onlyHealthPlanFields?: boolean
) => {
  const defaultValues = useMemo(
    () => ({
      ...initialValues,
    }),
    [initialValues]
  );

  const schema = useMemo(
    () =>
      onlyHealthPlanFields
        ? getHealthPlanOnlySchema(validationParams || {})
        : getEnrollmentFormSchema(validationParams || {}),
    [validationParams, onlyHealthPlanFields]
  );

  const methods = useForm({
    defaultValues,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema as any),
  });

  return { methods, defaultValues };
};
